import React, { useState, useEffect } from 'react';
import { orgAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Plus,
  Loader2,
  Building2,
  Tag,
  Calendar,
  X,
  FileText,
  Hash,
  Globe,
  Phone,
  Mail,
  MapPin,
  Pencil,
  ChevronRight,
  Receipt,
  ArrowRight,
} from 'lucide-react';

const Organizations = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [invoiceSequences, setInvoiceSequences] = useState([]);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [generatedNumber, setGeneratedNumber] = useState(null);
  const [newDept, setNewDept] = useState('');
  const [seqLoading, setSeqLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '', code: '',
    departments: ['Finance', 'Sales', 'IT', 'HR', 'Management'],
  });

  const [editData, setEditData] = useState({});
  const [newSeq, setNewSeq] = useState({ prefix: '', fiscal_year: '', description: '' });

  useEffect(() => { fetchOrganizations(); }, []);

  const fetchOrganizations = async () => {
    try {
      const response = await orgAPI.getOrganizations();
      setOrganizations(response.data);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    } finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.code) return;
    try {
      await orgAPI.createOrganization(formData);
      setDialogOpen(false);
      setFormData({ name: '', code: '', departments: ['Finance', 'Sales', 'IT', 'HR', 'Management'] });
      fetchOrganizations();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create organization');
    }
  };

  const handleEditOrg = async (org) => {
    try {
      const res = await orgAPI.getOrganization(org.id);
      setEditingOrg(res.data);
      setEditData({
        name: res.data.name || '',
        legal_name: res.data.legal_name || '',
        address: res.data.address || '',
        city: res.data.city || '',
        state: res.data.state || '',
        country: res.data.country || '',
        pincode: res.data.pincode || '',
        phone: res.data.phone || '',
        email: res.data.email || '',
        website: res.data.website || '',
        gst_number: res.data.gst_number || '',
        pan_number: res.data.pan_number || '',
        invoice_prefix: res.data.invoice_prefix || '',
        fiscal_year_start_month: res.data.fiscal_year_start_month || 4,
      });
      setEditDialogOpen(true);
    } catch { alert('Failed to load organization details'); }
  };

  const handleSaveEdit = async () => {
    if (!editingOrg) return;
    try {
      await orgAPI.updateOrganization(editingOrg.id, editData);
      setEditDialogOpen(false);
      setEditingOrg(null);
      fetchOrganizations();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleOpenInvoice = async (org) => {
    setSelectedOrg(org);
    setInvoiceDialogOpen(true);
    setGeneratedNumber(null);
    setSeqLoading(true);
    try {
      const [seqRes, previewRes] = await Promise.all([
        orgAPI.getInvoiceSequences(org.id),
        orgAPI.previewInvoiceNumber(org.id),
      ]);
      setInvoiceSequences(seqRes.data);
      setInvoicePreview(previewRes.data);
      setNewSeq({
        prefix: previewRes.data.prefix || org.invoice_prefix || org.code?.slice(0, 4).toUpperCase() || '',
        fiscal_year: previewRes.data.fiscal_year || '',
        description: '',
      });
    } catch { console.error('Failed to load invoice data'); }
    finally { setSeqLoading(false); }
  };

  const handleCreateSequence = async () => {
    if (!newSeq.prefix || !newSeq.fiscal_year || !selectedOrg) return;
    try {
      await orgAPI.createInvoiceSequence(selectedOrg.id, newSeq);
      const seqRes = await orgAPI.getInvoiceSequences(selectedOrg.id);
      setInvoiceSequences(seqRes.data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create sequence');
    }
  };

  const handleGenerateNumber = async (seqId) => {
    if (!selectedOrg) return;
    try {
      const res = await orgAPI.getNextInvoiceNumber(selectedOrg.id, seqId);
      setGeneratedNumber(res.data.invoice_number);
      const previewRes = await orgAPI.previewInvoiceNumber(selectedOrg.id);
      setInvoicePreview(previewRes.data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to generate number');
    }
  };

  const addDepartment = () => {
    if (!newDept.trim() || formData.departments.includes(newDept.trim())) return;
    setFormData(prev => ({ ...prev, departments: [...prev.departments, newDept.trim()] }));
    setNewDept('');
  };

  const removeDepartment = (dept) => {
    setFormData(prev => ({ ...prev, departments: prev.departments.filter(d => d !== dept) }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="organizations-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <span className="font-mono text-[10px] text-slate-400 uppercase tracking-[0.2em]">Settings</span>
          <h1 className="font-heading text-3xl lg:text-4xl font-black tracking-tight text-slate-900 mt-1">
            Organizations
          </h1>
          <p className="text-sm text-slate-500 mt-1">Manage profiles, departments, and invoice sequences</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 rounded-md bg-[#002FA7] hover:bg-[#002585] transition-all hover:-translate-y-[1px]" data-testid="add-org-btn">
              <Plus className="h-4 w-4" /> Add Organization
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-md max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold">Create Organization</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">Name *</label>
                  <Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                    className="mt-1.5 rounded-md" placeholder="Organization name" data-testid="org-name-input" />
                </div>
                <div>
                  <label className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">Code *</label>
                  <Input value={formData.code} onChange={(e) => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                    className="mt-1.5 rounded-md" placeholder="ORG_CODE" data-testid="org-code-input" />
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">Departments</label>
                <div className="flex flex-wrap gap-1.5 mt-2 mb-3">
                  {formData.departments.map((dept) => (
                    <Badge key={dept} variant="secondary" className="gap-1 rounded-full text-xs">
                      {dept}
                      <button onClick={() => removeDepartment(dept)} className="ml-0.5 hover:text-red-500"><X className="h-3 w-3" /></button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={newDept} onChange={(e) => setNewDept(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addDepartment())}
                    className="rounded-md" placeholder="Add department" data-testid="dept-input" />
                  <Button type="button" variant="outline" onClick={addDepartment} className="rounded-md">Add</Button>
                </div>
              </div>
              <Button onClick={handleSubmit} className="w-full rounded-md bg-[#002FA7] hover:bg-[#002585]" data-testid="create-org-btn">
                Create Organization
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Organizations Grid */}
      {organizations.length === 0 ? (
        <div className="border border-dashed rounded-lg p-16 text-center">
          <Building2 className="h-10 w-10 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-400 text-sm">No organizations yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {organizations.map((org) => (
            <div key={org.id} className="rounded-lg border border-slate-200 bg-white hover:border-slate-300 transition-colors" data-testid={`org-card-${org.id}`}>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg bg-[#002FA7]/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-[#002FA7]" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-slate-900">{org.name}</h3>
                      <p className="text-xs text-slate-400 font-mono tracking-wider">{org.code}</p>
                    </div>
                  </div>
                  <Badge variant={org.is_active ? 'default' : 'secondary'}
                    className={`rounded-full text-[10px] ${org.is_active ? 'bg-green-50 text-green-700 border-green-200' : ''}`}>
                    {org.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>

                {/* Details */}
                <div className="space-y-2 text-sm">
                  {org.legal_name && (
                    <div className="flex items-center gap-2 text-slate-500">
                      <FileText className="h-3.5 w-3.5 text-slate-400" />
                      <span>{org.legal_name}</span>
                    </div>
                  )}
                  {org.email && (
                    <div className="flex items-center gap-2 text-slate-500">
                      <Mail className="h-3.5 w-3.5 text-slate-400" />
                      <span>{org.email}</span>
                    </div>
                  )}
                  {org.phone && (
                    <div className="flex items-center gap-2 text-slate-500">
                      <Phone className="h-3.5 w-3.5 text-slate-400" />
                      <span>{org.phone}</span>
                    </div>
                  )}
                  {(org.city || org.state) && (
                    <div className="flex items-center gap-2 text-slate-500">
                      <MapPin className="h-3.5 w-3.5 text-slate-400" />
                      <span>{[org.city, org.state].filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                  {org.website && (
                    <div className="flex items-center gap-2 text-slate-500">
                      <Globe className="h-3.5 w-3.5 text-slate-400" />
                      <span>{org.website}</span>
                    </div>
                  )}
                </div>

                {/* Departments */}
                <div className="mt-4">
                  <span className="font-mono text-[10px] font-semibold text-slate-400 uppercase tracking-[0.15em] flex items-center gap-1">
                    <Tag className="h-3 w-3" /> Departments
                  </span>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {org.departments?.map((dept) => (
                      <Badge key={dept} variant="outline" className="rounded-full text-[11px] border-slate-200 text-slate-500">{dept}</Badge>
                    ))}
                  </div>
                </div>

                {/* Invoice Prefix */}
                {org.invoice_prefix && (
                  <div className="mt-4 p-3 rounded-md bg-slate-50 border border-slate-100 flex items-center gap-3">
                    <Hash className="h-4 w-4 text-[#002FA7]" />
                    <div>
                      <span className="font-mono text-[10px] text-slate-400 uppercase tracking-wider">Invoice Prefix</span>
                      <p className="text-sm font-heading font-bold text-slate-800">{org.invoice_prefix}-YYMM-####</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Card Actions Footer */}
              <div className="px-6 py-3 border-t border-slate-100 flex items-center gap-3">
                <button onClick={() => handleEditOrg(org)}
                  className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#002FA7] transition-colors"
                  data-testid={`edit-org-${org.id}`}>
                  <Pencil className="h-3.5 w-3.5" /> Edit Profile
                </button>
                <button onClick={() => handleOpenInvoice(org)}
                  className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#002FA7] transition-colors"
                  data-testid={`invoice-seq-${org.id}`}>
                  <Receipt className="h-3.5 w-3.5" /> Invoice Sequences
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Organization Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="rounded-md max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Edit Organization</DialogTitle>
          </DialogHeader>
          {editingOrg && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldBlock label="Display Name" value={editData.name} onChange={(v) => setEditData(p => ({ ...p, name: v }))} testId="edit-org-name" />
                <FieldBlock label="Legal Name" value={editData.legal_name} onChange={(v) => setEditData(p => ({ ...p, legal_name: v }))} testId="edit-org-legal" />
              </div>
              <FieldBlock label="Address" value={editData.address} onChange={(v) => setEditData(p => ({ ...p, address: v }))} testId="edit-org-address" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <FieldBlock label="City" value={editData.city} onChange={(v) => setEditData(p => ({ ...p, city: v }))} testId="edit-org-city" />
                <FieldBlock label="State" value={editData.state} onChange={(v) => setEditData(p => ({ ...p, state: v }))} testId="edit-org-state" />
                <FieldBlock label="Pincode" value={editData.pincode} onChange={(v) => setEditData(p => ({ ...p, pincode: v }))} testId="edit-org-pincode" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldBlock label="Phone" value={editData.phone} onChange={(v) => setEditData(p => ({ ...p, phone: v }))} testId="edit-org-phone" />
                <FieldBlock label="Email" value={editData.email} onChange={(v) => setEditData(p => ({ ...p, email: v }))} testId="edit-org-email" />
              </div>
              <FieldBlock label="Website" value={editData.website} onChange={(v) => setEditData(p => ({ ...p, website: v }))} testId="edit-org-website" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldBlock label="GST Number" value={editData.gst_number} onChange={(v) => setEditData(p => ({ ...p, gst_number: v }))} testId="edit-org-gst" />
                <FieldBlock label="PAN Number" value={editData.pan_number} onChange={(v) => setEditData(p => ({ ...p, pan_number: v }))} testId="edit-org-pan" />
              </div>
              <div className="pt-3 border-t border-slate-100">
                <span className="font-mono text-[10px] font-semibold text-[#002FA7] uppercase tracking-[0.2em]">Invoice Settings</span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                  <FieldBlock label="Invoice Prefix" value={editData.invoice_prefix} placeholder="e.g. IVKS"
                    onChange={(v) => setEditData(p => ({ ...p, invoice_prefix: v.toUpperCase() }))} testId="edit-org-inv-prefix" />
                  <div>
                    <label className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">FY Start Month</label>
                    <select value={editData.fiscal_year_start_month}
                      onChange={(e) => setEditData(p => ({ ...p, fiscal_year_start_month: parseInt(e.target.value) }))}
                      className="mt-1.5 w-full h-9 border border-slate-200 rounded-md px-3 text-sm outline-none focus:border-[#002FA7]"
                      data-testid="edit-org-fy-month">
                      {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                        <option key={i+1} value={i+1}>{m} ({i+1})</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <Button onClick={handleSaveEdit} className="w-full rounded-md bg-[#002FA7] hover:bg-[#002585]" data-testid="save-org-btn">
                Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invoice Sequence Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="rounded-md max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold flex items-center gap-2">
              <Receipt className="h-5 w-5 text-[#002FA7]" />
              Invoice Sequences {selectedOrg && `— ${selectedOrg.name}`}
            </DialogTitle>
          </DialogHeader>

          {seqLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          ) : (
            <div className="space-y-5 mt-3">
              {/* Preview */}
              {invoicePreview && (
                <div className="rounded-lg bg-[#0A0A0A] p-5 text-white" data-testid="invoice-preview-card">
                  <span className="font-mono text-[10px] text-white/40 uppercase tracking-[0.2em]">Next Invoice Number</span>
                  <div className="font-heading text-2xl font-bold mt-2 tracking-wide">{invoicePreview.preview}</div>
                  <div className="flex items-center gap-4 mt-3 text-white/40 text-xs font-mono">
                    <span>Prefix: {invoicePreview.prefix}</span>
                    <span>FY: {invoicePreview.fiscal_year}</span>
                    <span>Seq: #{invoicePreview.next_sequence}</span>
                  </div>
                </div>
              )}

              {/* Generated Number Alert */}
              {generatedNumber && (
                <div className="rounded-md bg-green-50 border border-green-200 p-3 flex items-center gap-2" data-testid="generated-number">
                  <Hash className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-800 font-medium">Generated: <span className="font-mono font-bold">{generatedNumber}</span></span>
                </div>
              )}

              {/* Existing Sequences */}
              {invoiceSequences.length > 0 && (
                <div>
                  <span className="font-mono text-[10px] text-slate-400 uppercase tracking-[0.15em]">Active Sequences</span>
                  <div className="space-y-2 mt-2">
                    {invoiceSequences.map(seq => (
                      <div key={seq.id} className="flex items-center justify-between p-3 rounded-md border border-slate-200 bg-white">
                        <div>
                          <span className="text-sm font-heading font-bold text-slate-800">{seq.prefix}-{seq.fiscal_year}</span>
                          <span className="text-xs text-slate-400 ml-2">Current: #{seq.current_number}</span>
                        </div>
                        <button onClick={() => handleGenerateNumber(seq.id)}
                          className="text-xs font-medium text-[#002FA7] hover:underline flex items-center gap-1"
                          data-testid={`gen-next-${seq.id}`}>
                          Generate Next <ArrowRight className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Create New Sequence */}
              <div className="pt-3 border-t border-slate-100">
                <span className="font-mono text-[10px] text-slate-400 uppercase tracking-[0.15em]">Create New Sequence</span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                  <Input value={newSeq.prefix} onChange={(e) => setNewSeq(p => ({ ...p, prefix: e.target.value.toUpperCase() }))}
                    placeholder="Prefix (e.g. IVKS)" className="rounded-md" data-testid="new-seq-prefix" />
                  <Input value={newSeq.fiscal_year} onChange={(e) => setNewSeq(p => ({ ...p, fiscal_year: e.target.value }))}
                    placeholder="FY (e.g. 2526)" className="rounded-md" data-testid="new-seq-fy" />
                </div>
                <Input value={newSeq.description} onChange={(e) => setNewSeq(p => ({ ...p, description: e.target.value }))}
                  placeholder="Description (optional)" className="rounded-md mt-2" data-testid="new-seq-desc" />
                <Button onClick={handleCreateSequence} className="mt-3 rounded-md bg-[#002FA7] hover:bg-[#002585]" data-testid="create-seq-btn">
                  <Plus className="h-4 w-4 mr-1" /> Create Sequence
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const FieldBlock = ({ label, value, onChange, placeholder, testId }) => (
  <div>
    <label className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{label}</label>
    <Input value={value || ''} onChange={(e) => onChange(e.target.value)}
      className="mt-1.5 rounded-md" placeholder={placeholder || label} data-testid={testId} />
  </div>
);

export default Organizations;
